export type RetailVertical =
  | 'Accessories & Jewelry'
  | 'Apparel Contemporary Fashion'
  | 'Apparel - Fast Fashion'
  | 'Apparel - Kids'
  | 'Apparel - Luxury Fashion'
  | 'Apparel - Western'
  | 'Beauty & Personal Care'
  | 'Electronics'
  | 'Fabric, Quilt & Yarn'
  | 'Footwear'
  | 'Home & Garden'
  | 'Kitchen'
  | 'Other Retail'
  | 'Sports & Outdoor'
  | 'Toys & Hobby'

export type VerticalBenchmarks = {
  salesYoYPct: number
  imuPct: number
  markdownPct: number
  grossMarginPct: number
  cashMarginPct: number
  inventoryTurn: number
  freshnessPct: number
  freshnessWindowDays: 60 | 90
  avgOrderValue: number
}

export type HealthMetricResult = {
  label: string
  score: number
  weight: number
  actual: number
  benchmark: number
  variance: number
  lowerIsBetter: boolean
}

export type HealthScoreResult = {
  totalScore: number
  metrics: HealthMetricResult[]
}

export const VERTICAL_BENCHMARKS: Record<RetailVertical, VerticalBenchmarks> = {
  'Accessories & Jewelry': { salesYoYPct: 10, imuPct: 67, markdownPct: 10, grossMarginPct: 63.7, cashMarginPct: 60, inventoryTurn: 3.0, freshnessPct: 53, freshnessWindowDays: 90, avgOrderValue: 210 },
  'Apparel Contemporary Fashion': { salesYoYPct: 9, imuPct: 55, markdownPct: 13, grossMarginPct: 49.2, cashMarginPct: 50, inventoryTurn: 3.3, freshnessPct: 58, freshnessWindowDays: 60, avgOrderValue: 140 },
  'Apparel - Fast Fashion': { salesYoYPct: 12, imuPct: 65, markdownPct: 20, grossMarginPct: 58.0, cashMarginPct: 54, inventoryTurn: 4.2, freshnessPct: 72, freshnessWindowDays: 60, avgOrderValue: 88 },
  'Apparel - Kids': { salesYoYPct: 8, imuPct: 60, markdownPct: 18, grossMarginPct: 52.8, cashMarginPct: 51, inventoryTurn: 4.0, freshnessPct: 70, freshnessWindowDays: 60, avgOrderValue: 65 },
  'Apparel - Luxury Fashion': { salesYoYPct: 7, imuPct: 60, markdownPct: 14, grossMarginPct: 54.4, cashMarginPct: 53, inventoryTurn: 3.1, freshnessPct: 52, freshnessWindowDays: 60, avgOrderValue: 155 },
  'Apparel - Western': { salesYoYPct: 7, imuPct: 56, markdownPct: 14, grossMarginPct: 49.8, cashMarginPct: 52, inventoryTurn: 2.7, freshnessPct: 58, freshnessWindowDays: 60, avgOrderValue: 140 },
  'Beauty & Personal Care': { salesYoYPct: 12, imuPct: 63, markdownPct: 7, grossMarginPct: 60.4, cashMarginPct: 59, inventoryTurn: 4.2, freshnessPct: 72, freshnessWindowDays: 60, avgOrderValue: 68 },
  Electronics: { salesYoYPct: 4, imuPct: 37, markdownPct: 8, grossMarginPct: 32.0, cashMarginPct: 34, inventoryTurn: 6.0, freshnessPct: 68, freshnessWindowDays: 60, avgOrderValue: 265 },
  'Fabric, Quilt & Yarn': { salesYoYPct: 5, imuPct: 58, markdownPct: 6, grossMarginPct: 55.5, cashMarginPct: 55, inventoryTurn: 2.8, freshnessPct: 45, freshnessWindowDays: 90, avgOrderValue: 48 },
  Footwear: { salesYoYPct: 6, imuPct: 56, markdownPct: 13, grossMarginPct: 50.3, cashMarginPct: 52, inventoryTurn: 2.7, freshnessPct: 58, freshnessWindowDays: 90, avgOrderValue: 130 },
  'Home & Garden': { salesYoYPct: 5, imuPct: 53, markdownPct: 12, grossMarginPct: 47.4, cashMarginPct: 47, inventoryTurn: 3.0, freshnessPct: 48, freshnessWindowDays: 90, avgOrderValue: 165 },
  Kitchen: { salesYoYPct: 6, imuPct: 55, markdownPct: 10, grossMarginPct: 50.5, cashMarginPct: 51, inventoryTurn: 3.2, freshnessPct: 52, freshnessWindowDays: 90, avgOrderValue: 85 },
  'Other Retail': { salesYoYPct: 6, imuPct: 57, markdownPct: 10, grossMarginPct: 52.7, cashMarginPct: 52, inventoryTurn: 3.5, freshnessPct: 58, freshnessWindowDays: 90, avgOrderValue: 95 },
  'Sports & Outdoor': { salesYoYPct: 6, imuPct: 53, markdownPct: 14, grossMarginPct: 46.4, cashMarginPct: 46, inventoryTurn: 3.2, freshnessPct: 58, freshnessWindowDays: 60, avgOrderValue: 120 },
  'Toys & Hobby': { salesYoYPct: 7, imuPct: 56, markdownPct: 16, grossMarginPct: 49.0, cashMarginPct: 48, inventoryTurn: 3.1, freshnessPct: 55, freshnessWindowDays: 90, avgOrderValue: 55 },
}

export const DEFAULT_VERTICAL: RetailVertical = 'Apparel Contemporary Fashion'

export const HEALTH_WEIGHTS = {
  cashMargin: 30,
  salesGrowth: 25,
  markdowns: 20,
  turn: 15,
  freshness: 10,
} as const

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function computeVariance(actual: number, benchmark: number, lowerIsBetter: boolean): number {
  if (benchmark === 0) return 0
  return lowerIsBetter ? (actual - benchmark) / benchmark : (benchmark - actual) / benchmark
}

function scoreMetric(label: string, actual: number, benchmark: number, weight: number, lowerIsBetter: boolean): HealthMetricResult {
  const variance = computeVariance(actual, benchmark, lowerIsBetter)
  const clamped = clamp(variance, 0, 1)
  return { label, score: Math.round((1 - clamped) * weight * 10) / 10, weight, actual, benchmark, variance, lowerIsBetter }
}

export type HealthScoreInputs = {
  grossMarginPct: number
  salesYoYPct: number
  markdownPct: number
  inventoryTurn: number
  freshnessPct: number
}

export function computeHealthScore(inputs: HealthScoreInputs, vertical: RetailVertical = DEFAULT_VERTICAL): HealthScoreResult {
  const b = VERTICAL_BENCHMARKS[vertical]
  const metrics: HealthMetricResult[] = [
    scoreMetric('Gross Margin', inputs.grossMarginPct, b.grossMarginPct, HEALTH_WEIGHTS.cashMargin, false),
    scoreMetric('Sales Growth', inputs.salesYoYPct, b.salesYoYPct, HEALTH_WEIGHTS.salesGrowth, false),
    scoreMetric('Markdowns', inputs.markdownPct, b.markdownPct, HEALTH_WEIGHTS.markdowns, true),
    scoreMetric('Turnover', inputs.inventoryTurn, b.inventoryTurn, HEALTH_WEIGHTS.turn, false),
    scoreMetric('Freshness Factor', inputs.freshnessPct, b.freshnessPct, HEALTH_WEIGHTS.freshness, false),
  ]
  return { totalScore: Math.round(metrics.reduce((sum, metric) => sum + metric.score, 0)), metrics }
}

export function healthMetricDisplayLabel(label: string): string {
  switch (label) {
    case 'Turnover': return 'Annual Turn'
    case 'Freshness Factor': return 'Inventory Freshness'
    default: return label
  }
}

export function metricScoreTone(score: number, weight: number): 'excellent' | 'veryGood' | 'average' | 'belowAverage' | 'critical' {
  const pct = weight > 0 ? score / weight : 0
  if (pct >= 0.8) return 'excellent'
  if (pct >= 0.6) return 'veryGood'
  if (pct >= 0.4) return 'average'
  if (pct >= 0.2) return 'belowAverage'
  return 'critical'
}

export function resolveClientVertical(client: any): RetailVertical {
  const candidate = client?.shopifyClient?.vertical || client?.clientExtra?.vertical || client?.client_extra?.vertical
  if (candidate && candidate in VERTICAL_BENCHMARKS) return candidate as RetailVertical
  return DEFAULT_VERTICAL
}